import { apiClient } from '@/shared/services/client'
import type {
  User,
  UserStats,
  PaginatedUsers,
  UserCreateInput,
  UserUpdateInput,
  GroupInfo,
  UsersFilters,
} from '@/modules/users/types/user'

/**
 * API functions for user management
 * Based on backend/routers/users.py endpoints
 */

// ============================================================================
// Users CRUD
// ============================================================================

/**
 * Get paginated list of users with filters
 */
export async function getUsers(filters?: UsersFilters): Promise<PaginatedUsers> {
  const response = await apiClient.get<PaginatedUsers>('/users/', {
    params: filters,
  })
  return response.data
}

/**
 * Get user statistics
 */
export async function getUserStats(): Promise<UserStats> {
  const response = await apiClient.get<UserStats>('/users/stats/')
  return response.data
}

/**
 * Get single user by ID
 */
export async function getUser(userId: number): Promise<User> {
  const response = await apiClient.get<User>(`/users/${userId}/`)
  return response.data
}

/**
 * Create new user
 */
export async function createUser(data: UserCreateInput): Promise<User> {
  const response = await apiClient.post<User>('/users/', data)
  return response.data
}

/**
 * Update user
 */
export async function updateUser(userId: number, data: UserUpdateInput): Promise<User> {
  const response = await apiClient.put<User>(`/users/${userId}/`, data)
  return response.data
}

/**
 * Partial update user
 */
export async function partialUpdateUser(userId: number, data: Partial<UserUpdateInput>): Promise<User> {
  const response = await apiClient.patch<User>(`/users/${userId}/`, data)
  return response.data
}

/**
 * Deactivate user (soft delete)
 */
export async function deleteUser(userId: number): Promise<{ message: string; success: boolean }> {
  const response = await apiClient.delete(`/users/${userId}/`)
  return response.data
}

/**
 * Activate user
 */
export async function activateUser(userId: number): Promise<{ message: string; success: boolean }> {
  const response = await apiClient.post(`/users/${userId}/activate/`)
  return response.data
}

// ============================================================================
// User Areas
// ============================================================================

/**
 * Get user's areas
 */
export async function getUserAreas(userId: number): Promise<{ id: number; nombre: string; slug: string }[]> {
  const response = await apiClient.get(`/users/${userId}/areas/`)
  return response.data
}

// ============================================================================
// User Groups
// ============================================================================

/**
 * Get user's groups
 */
export async function getUserGroups(userId: number): Promise<GroupInfo[]> {
  const response = await apiClient.get<GroupInfo[]>(`/users/${userId}/groups/`)
  return response.data
}

/**
 * Add user to group
 */
export async function addUserToGroup(
  userId: number,
  groupId: number
): Promise<{ message: string; success: boolean }> {
  const response = await apiClient.post(`/users/${userId}/groups/${groupId}/`)
  return response.data
}

/**
 * Remove user from group
 */
export async function removeUserFromGroup(
  userId: number,
  groupId: number
): Promise<{ message: string; success: boolean }> {
  const response = await apiClient.delete(`/users/${userId}/groups/${groupId}/`)
  return response.data
}
